import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PhxSelectOption, PhxSelectOptionWithMetaData, PhxSelectValue, PhxSelectionStates } from './select.types';
import { PhxStatsigService } from '../../services/phx-statsig.service';
import { PHX_STATSIG_KEYS } from '../../constants';
function isPhxSelectOption(option: PhxSelectValue | PhxSelectOption): option is PhxSelectOption {
  return option && typeof option === 'object' && 'value' in option;
}

@Injectable()
export class PhxSelectService {
  private _selectedItemsSet: Set<PhxSelectValue> = new Set();
  private _indeterminateItemsSet: Set<PhxSelectValue> = new Set();
  private _selectedOptions: PhxSelectOption[] = [];
  private _selectedOptionsMap = new Map<PhxSelectValue, PhxSelectOption>();
  private _filteredSelectedOptionKeys: PhxSelectValue[] = [];
  private _filteredSelectedOptions: PhxSelectOption[] = [];
  private _selectedValues$ = new BehaviorSubject<PhxSelectValue[]>([]);
  private _expandedOptions: PhxSelectOption[];
  private isCustomScrollEnabled = true;  // Feature flag to enable/disable custom virtual scroll strategy
  private isCustomTooltipEnabled = false;  // Feature flag to enable/disable custom tooltips provided with phx-option-item
  private isTooltipOnTruncateEnabled = false;  // Feature flag to enable/disable tooltip on truncate

  public expandedOptionKeys: Set<PhxSelectValue> = new Set<PhxSelectValue>();
  public allOptionsMap = new Map<PhxSelectValue, PhxSelectOptionWithMetaData>();
  public isNestedOptions = false;
  public readonly selectedValues$: Observable<PhxSelectValue[]> = this._selectedValues$.asObservable();

  constructor(private phxStatsigService: PhxStatsigService) {
    this.phxStatsigService.getConfigs().subscribe((config) => {
      this.isCustomScrollEnabled = config[PHX_STATSIG_KEYS.VIRTUAL_SCROLL_UPDATES] || this.isCustomScrollEnabled;
      this.isCustomTooltipEnabled = config[PHX_STATSIG_KEYS.PHX_SELECT_CUSTOM_TOOLTIP_ENABLED] || this.isCustomTooltipEnabled;
      this.isTooltipOnTruncateEnabled = config[PHX_STATSIG_KEYS.PHX_SELECT_TOOLTIP_ON_TRUNCATE_ENABLED] || this.isTooltipOnTruncateEnabled;
    });

    this._selectedValues$.subscribe((keys) => {
      this._filteredSelectedOptionKeys = this.getIndependentOptions(keys, this._selectedItemsSet);
      this._filteredSelectedOptions = this.convertValuesIntoOptions(this._filteredSelectedOptionKeys);
    });
  }

  public isCustomScrollStrategyEnabled(): boolean {
    return this.isCustomScrollEnabled;
  }

  public isCustomTooltipFeatureFlagEnabled(): boolean {
    return this.isCustomTooltipEnabled;
  }

  public isTooltipOnTruncateFeatureFlagEnabled(): boolean {
    return this.isTooltipOnTruncateEnabled;
  }

  private flattenOptions(options: PhxSelectOption[], parent: PhxSelectOptionWithMetaData | null = null): void {
    let totalDividersCount = 0;
    options.forEach((option) => {
      let opt: PhxSelectOptionWithMetaData = { ...option };
      if (parent) opt.ancestors = [...(parent.ancestors || []), parent.value];

      if (opt.groupLabel) this.allOptionsMap.set(opt.groupLabel, opt);
      else if (opt.isDivider) this.allOptionsMap.set(`phx-divider-${totalDividersCount++}`, opt);
      else this.allOptionsMap.set(opt.value, opt);

      if (opt.children && opt.children.length > 0) {
        this.isNestedOptions = true;
        opt.allChildren = new Set();
        this.flattenOptions(opt.children, opt);
        opt.allChildren = new Set(this.flatChildren(opt, []));
      }
    });
  }

  private flatChildren(option: PhxSelectOptionWithMetaData, result: PhxSelectValue[] = []): PhxSelectValue[] {
    if (option.children && option.children.length > 0) {
      for (let child of option.children) {
        if (child.value) {
          result.push(child.value);
          this.flatChildren(child, result);
        }
      }
    }
    return result;
  }

  private setAncestorsState(key: PhxSelectValue): void {
    const option: PhxSelectOptionWithMetaData = this.getOptionByKey(key);
    const optionParent: PhxSelectOptionWithMetaData = this.getParentByKey(key);

    if (optionParent) {
      const { allUnselected, someSelected, someIndeterminate } = this.getSelectionStatus(optionParent.children);

      if (someIndeterminate || someSelected) {
        option?.ancestors?.forEach((ancestor) => {
          this._selectedItemsSet.delete(ancestor);
          this._indeterminateItemsSet.add(ancestor);
        });
      } else if (allUnselected) {
        this._indeterminateItemsSet.delete(optionParent.value);
        this._selectedItemsSet.delete(optionParent.value);
        this.setAncestorsState(optionParent.value);
      }
    }
  }

  private setOptionAndItsChildrenState(key: PhxSelectValue, isMultiple: boolean): void {
    const option: PhxSelectOptionWithMetaData = this.getOptionByKey(key);

    if (!isMultiple) {
      this.clearSelectedOption(false);
      this.setSelectedOptionKey(option?.value);
      return;
    }
    if (this.isSelected(option?.value)) {
      this.setSelectedOptionKey(option?.value, false);
      if (option?.allChildren?.size) {
        option?.allChildren.forEach((value) => {
          this.setSelectedOptionKey(value, false);
        });
      }
    } else {
      this.setSelectedOptionKey(option?.value);
      if (option?.allChildren?.size) {
        option?.allChildren.forEach((value) => {
          if (!this.isSelected(value)) {
            this.setSelectedOptionKey(value);
          }
        });
      }
    }
  }

  private convertValuesIntoOptions(optionsKeys: PhxSelectValue[]): PhxSelectOption[] {
    const selectedOptions: PhxSelectOption[] = [];
    optionsKeys.forEach((key) => {
      const option: PhxSelectOptionWithMetaData = this.getOptionByKey(key);
      if (option) {
        selectedOptions.push(this.removeMetaFields(option));
      }
    });
    return selectedOptions;
  }

  private getSelectionStatus(options: PhxSelectOption[]): PhxSelectionStates {
    let selectedCount = 0;
    let unselectedCount = 0;
    let someIndeterminate = false;

    const optionsWithoutGroupLabel = options.filter((opt) => !opt.groupLabel);

    optionsWithoutGroupLabel.forEach((opt) => {
      if (!someIndeterminate && this._indeterminateItemsSet.has(opt.value)) someIndeterminate = true;
      if (this._selectedItemsSet.has(opt.value)) selectedCount += 1;
      else unselectedCount += 1;
    });

    const allSelected = selectedCount === optionsWithoutGroupLabel.length;
    const allUnselected = unselectedCount === optionsWithoutGroupLabel.length;

    return {
      allSelected,
      allUnselected,
      someIndeterminate,
      someSelected: !allUnselected || allSelected,
    };
  }

  private preSelectOption(value: PhxSelectValue | PhxSelectOption, isMultiple: boolean, isOptimisticSelectionEnabled: boolean): void {
    if (isOptimisticSelectionEnabled && isPhxSelectOption(value)) {
      this.setSelectedOptionKey(value.value);
      return;
    }

    if (!isPhxSelectOption(value)) {
      const optionToSelect = this.getOptionByKey(value);
      if (!optionToSelect) return;
      if (this.isNestedOptions) {
        if (!this.isSelected(optionToSelect.value)) {
          this.setSelectionState(value, isMultiple, false);
        }
      } else {
        this.setSelectedOptionKey(optionToSelect.value);
      }
    }
  }

  private removeMetaFields(option: PhxSelectOptionWithMetaData): PhxSelectOption {
    const { allChildren, ancestors, ...rest } = option;
    return rest;
  }

  private getIndependentOptions(options: PhxSelectValue[], selectedItemsSet: Set<PhxSelectValue>): PhxSelectValue[] {
    const filteredValues: PhxSelectValue[] = options.filter((value) => {
      const optionParent: PhxSelectOptionWithMetaData = this.getParentByKey(value);
      if (optionParent && selectedItemsSet.has(optionParent.value)) return false;
      return true;
    });

    return filteredValues;
  }

  private traverseTreeToExpand(option: PhxSelectOption): void {
    const optionIndex = this._expandedOptions.findIndex((opt) => opt.value === option.value);
    this._expandedOptions.splice(optionIndex + 1, 0, ...option.children);
    option.children.forEach((child) => {
      if (this.expandedOptionKeys.has(child.value)) this.traverseTreeToExpand(child);
    });
  }

  setOptions(options: PhxSelectOption[]): void {
    this.allOptionsMap.clear();
    this.flattenOptions(options);
  }

  setSelectedOptionsMap(options: PhxSelectOption[]): void {
    this._selectedOptionsMap.clear();
    options.forEach((option) => {
      this._selectedOptionsMap.set(option?.value, option);
    });
  }

  getOptionByKey(key: PhxSelectValue): PhxSelectOptionWithMetaData {
    return this.allOptionsMap.get(key) || this._selectedOptionsMap.get(key);
  }

  getParentByKey(key: PhxSelectValue): PhxSelectOptionWithMetaData {
    const option = this.getOptionByKey(key);
    const parent = option?.ancestors ? option.ancestors[option.ancestors.length - 1] : undefined;
    if (!parent) return undefined;
    return this.getOptionByKey(parent);
  }

  setSelectedOptionKey(key: PhxSelectValue, selected: boolean = true): void {
    if (selected) {
      this._selectedItemsSet.add(key);
    } else {
      this._selectedItemsSet.delete(key);
    }
    this._indeterminateItemsSet.delete(key);
  }

  isSelected(key: PhxSelectValue): boolean {
    return this._selectedItemsSet.has(key);
  }

  isIndeterminate(key: PhxSelectValue): boolean {
    return this._indeterminateItemsSet.has(key);
  }

  clearSelectedOption(shouldEmit: boolean = true): void {
    this._selectedItemsSet.clear();
    this._indeterminateItemsSet.clear();
    this._selectedOptions = [];
    this._selectedOptionsMap.clear();
    if (shouldEmit) this._selectedValues$.next([]);
  }

  setSelectionState(key: PhxSelectValue, isMultiple: boolean, shouldConvertValuesIntoOptions: boolean = true): void {
    /** Order is important here */
    this.setOptionAndItsChildrenState(key, isMultiple);
    this.setAncestorsState(key);
    if (shouldConvertValuesIntoOptions) {
      this._selectedOptions = this.convertValuesIntoOptions(this.selectedOptionKeys);
      this.setSelectedOptionsMap(this._selectedOptions);
      this._selectedValues$.next(this.selectedOptionKeys);
    }
  }

  setPreSelectedOptions(
    values: PhxSelectValue[] | PhxSelectOption[],
    isMultiple: boolean,
    isIndependentSelectionEnabled: boolean,
    isOptimisticSelectionEnabled: boolean
  ): void {
    let selectedValues = values;

    if (this.isNestedOptions && !isIndependentSelectionEnabled) {
      const selectedOptionsSet = new Set<PhxSelectValue>(values as PhxSelectValue[]);
      selectedValues = this.getIndependentOptions(values as PhxSelectValue[], selectedOptionsSet);
    }

    selectedValues?.forEach((item: PhxSelectValue | PhxSelectOption): void => {
      this.preSelectOption(item, isMultiple, isOptimisticSelectionEnabled);
    });

    this._selectedOptions = isOptimisticSelectionEnabled ? (values as PhxSelectOption[]) : this.convertValuesIntoOptions(this.selectedOptionKeys);

    if (isOptimisticSelectionEnabled) this.setSelectedOptionsMap((values as PhxSelectOption[]) || []);
    else this.setSelectedOptionsMap(this._selectedOptions);

    this._selectedValues$.next(this.selectedOptionKeys);
  }

  getAllChildren(key: string): PhxSelectOption[] {
    const option = this.getOptionByKey(key);
    const result = [];
    for (let childKey of option.allChildren) {
      result.push(this.removeMetaFields(this.getOptionByKey(childKey)));
    }
    return result;
  }

  getAllAncestors(key: string): PhxSelectOption[] {
    const option = this.getOptionByKey(key);
    const result = [];
    if (option?.ancestors) {
      for (let ancestorKey of option.ancestors) {
        result.push(this.removeMetaFields(this.getOptionByKey(ancestorKey)));
      }
    }
    return result;
  }

  expandTreeChildren(option: PhxSelectOption, currentOptions: PhxSelectOption[]): PhxSelectOption[] {
    this._expandedOptions = [...currentOptions];
    this.traverseTreeToExpand(option);
    this.expandedOptionKeys.add(option.value);

    return this._expandedOptions;
  }

  collapseTreeChildren(option: PhxSelectOption, currentOptions: PhxSelectOption[]): PhxSelectOption[] {
    const filteredOptions = currentOptions.filter((opt) => {
      const optAncestors = this.getAllAncestors(opt.value);
      if (!optAncestors?.length) return true;
      return !optAncestors.some((item) => item.value === option.value);
    });
    this.expandedOptionKeys.delete(option.value);

    return filteredOptions;
  }

  get selectedItemsCount(): number {
    return this._selectedItemsSet.size;
  }

  get firstSelectedOption(): PhxSelectOption {
    const [first] = this._selectedOptions;
    return first || null;
  }

  get selectedOptions(): PhxSelectOption[] {
    return this._selectedOptions;
  }

  get selectedOptionKeys(): PhxSelectValue[] {
    return Array.from(this._selectedItemsSet);
  }

  get filteredSelectedOptionKeys(): PhxSelectValue[] {
    return this._filteredSelectedOptionKeys;
  }

  get filteredSelectedOptions(): PhxSelectOption[] {
    return this._filteredSelectedOptions;
  }
}
